/**
 * The calendar column's rough layer (`calendar-rough-layer`;
 * stage 1 of the progressive columns plan, research-progressive-columns-20261010).
 *
 * A strip stays at opacity 0 until its last day unit lands (reveal when
 * complete), so a fresh column shows only its skeleton for the whole time its
 * day units wait for frame budget, though its events were installed long
 * before. With this flag on, the strip's bind write at data arrival
 * (`bindQueued`, stage 1b, dom 65; no strip unit needed) reads every day
 * fragment once (the read the day units then paint from), lays the events
 * into the layout group's own lanes and writes one gradient-stack layer: a
 * neutral box per timed event at the rect its chip will take, under a blur.
 * The layer is a sibling of the strip in `CalendarSheetColumn` (outside its
 * opacity), and the strip's reveal clears it in the same write that shows
 * the chips.
 *
 * - Geometry has one owner, the one the cells use: each box is read from
 *   its plan entry's lane style (`CalendarColumnLayoutGroup.Plan.generate`:
 *   x offset, end inset, extra height, top margin; `RoughGeometry.cellBox`,
 *   calendar-rough-layer-geometry.ts)
 *   and `PIXELS_PER_HOUR`, with times from the window's truncation
 *   (`DateFragment.Windowed.truncate`, as `visibleSpanEntries`), the
 *   column's offset (`getPositioning`) and the cell's y offset. Widths are
 *   fractions of the layer's width plus pixels, so a resize needs no rewrite
 *   and nothing is measured.
 * - Stage 1 draws the event subcolumn only (a subcolumn with `roughSpans`), in
 *   a neutral tint; a full-day overlay band (the sticker layer) takes no
 *   share; a strip whose other bands are not all flex lanes draws nothing.
 * - Removal: `RoughLayer.clear` from the column's presentation end (the
 *   reveal's own write), a new date's load, a recycle; a stale column's layer
 *   is clipped like its strip (`content-stale`).
 */

import {
  DateFragment,
  DateTime,
  DurationTime,
  type GenericRange,
  type NaiveDateTime,
  type TimeOfDay,
  TimePoint,
  type TimezoneRegion,
} from "@tento-chrono";
import { CalendarColumnLayoutGroup } from "@tento-ui/components/calendar/calendar-column-layout-group";
import { PIXELS_PER_HOUR } from "@tento-ui/components/calendar/calendar-hour";
import { DomFlags } from "@tento-ui/dom-flags";
import type { Option } from "@tento-lona/sheets";
import type { CalendarSubcolumn } from "@tento-lona/sheets-ui/sheet/calendar-subcolumn.ts";
import { RoughGeometry } from "./calendar-rough-layer-geometry.ts";

/**
 * Whether strips draw rough boxes before their chips, from the
 * `calendar-rough-layer` DOM flag (`DomFlags`, off by default; `?flag:` or
 * localStorage `debug-flag:`). Read when a strip binds; the page value is read
 * once, so a stored change applies on reload.
 */
export function roughLayerEnabled(): boolean {
  return DomFlags.read("calendar-rough-layer", false);
}

/** The blur over the boxes (CSS px). */
export const ROUGH_BLUR_PX = 5;

/**
 * One box in a column's rough layer, in the layer's box: `x = gx·W + bx` and
 * `width = fw·W + aw`, with `W` the layer's width in px; `y` and `height` in px.
 */
export type RoughBox = {
  readonly id: string;
  readonly gx: number;
  readonly bx: number;
  readonly fw: number;
  readonly aw: number;
  readonly y: number;
  readonly height: number;
};

/** Where boxes come from and how they are laid out (pure). */
export namespace RoughBoxes {
  /** A layout group's share of the layer's width: where it starts and how wide it is (fractions). */
  export type Slot = { readonly left: number; readonly width: number };

  /** The slot of band `band` of `bands` equal flex lanes in day `day` of `days`. */
  export function slot(day: number, days: number, band: number, bands: number): Slot {
    return { left: day / days + band / (days * bands), width: 1 / (days * bands) };
  }

  /**
   * The boxes of `spans` in one layer: one per span the window shows, at the
   * rect its cell takes in the layout group at `slot` (`RoughGeometry.cellBox` of the
   * group's plan entry for it).
   */
  export function forLayer(
    spans: readonly CalendarSubcolumn.RoughSpan[],
    windowed: DateFragment.Windowed,
    slot: Slot,
  ): RoughBox[] {
    const positioning = windowed.getPositioning(PIXELS_PER_HOUR);
    if (positioning == null) return [];
    const infos: CalendarColumnLayoutGroup.LayoutInfo[] = [];
    for (const span of spans) {
      const tod = windowed.truncate(new DateTime.Range(span.start, span.end ?? span.start));
      if (tod == null) continue;
      const time = TimePoint.Range.fromTodRange(tod);
      infos.push(new CalendarColumnLayoutGroup.LayoutInfo(
        span.id,
        new DurationTime.Range(time.start.toDuration(), time.start.add(time.duration).toDuration()),
      ));
    }
    // Positional: with no previous plan the identity mode decides only whether
    // a duplicate span id throws, and nothing may throw in the bind's write.
    const plan = CalendarColumnLayoutGroup.Plan.generate(infos, null, "positional");
    // The column's translate plus the cell's `--std-calendar-cell-y-offset`.
    const top = positioning.transformY - windowed.windowedStartHrs * PIXELS_PER_HOUR;
    const boxes: RoughBox[] = [];
    for (const entry of plan.entries) {
      const box = RoughGeometry.cellBox(entry, slot, top);
      if (box.height > 0) boxes.push(box);
    }
    return boxes;
  }

  /** What a strip's rough unit reads its boxes from. */
  export type StripInput = {
    /** The strip's days, in order: each day's region and fragments. */
    readonly days: readonly {
      readonly tz: TimezoneRegion;
      readonly frags: readonly { readonly frag: Option<DateFragment> }[];
    }[];
    readonly subcolumns: readonly CalendarSubcolumn[];
    readonly window: TimeOfDay.Range;
    readonly partialWindow: GenericRange<Option<NaiveDateTime>>;
    /** The span context a band would paint from (the bind's cache and range). */
    readonly context: (windowed: DateFragment.Windowed, tz: TimezoneRegion) => CalendarSubcolumn.SpanRenderContext;
  };

  /**
   * Whether `subcolumn` is a full-day overlay that takes no share of the
   * day's width: `stagger: false` with natural width, the sticker overlay's
   * shape (its provider positions its layer absolutely over the whole day).
   */
  export function overlay(subcolumn: CalendarSubcolumn): boolean {
    return subcolumn.stagger === false && subcolumn.width.type === "natural";
  }

  /**
   * Every box of a strip, or null when the bands that share the day are not
   * all flex lanes (their widths are not known without layout): stage 1
   * draws nothing then. Overlays (`overlay`) take no share and draw nothing.
   */
  export function forStrip(input: StripInput): Option<RoughBox[]> {
    const { days } = input;
    const lanes = input.subcolumns.filter((s) => !overlay(s));
    if (lanes.some((s) => s.width.type !== "flex" || s.stagger === false)) return null;
    const boxes: RoughBox[] = [];
    days.forEach(({ tz, frags }, day) => {
      for (const { frag } of frags) {
        if (frag == null) continue;
        const windowed = new DateFragment.Windowed(frag, input.partialWindow, input.window);
        lanes.forEach((subcolumn, band) => {
          const spans = subcolumn.roughSpans?.(input.context(windowed, tz));
          if (spans == null) return;
          boxes.push(...forLayer(spans, windowed, slot(day, days.length, band, lanes.length)));
        });
      }
    });
    return boxes;
  }
}

/** A rounded CSS number. */
function num(value: number): string {
  return String(Math.round(value * 1000) / 1000);
}

/**
 * The layer's three inline declarations for `boxes`: one `--rough-box` image
 * per box, sized and placed by its rect. A background position percentage
 * places an image at `p·(W - w)`, so the box's `gx·W + bx` is written as
 * `calc(P% + B px)` with `P = gx / (1 - fw)` and `B = bx + P·aw`.
 */
export function roughLayerStyle(boxes: readonly RoughBox[]): {
  readonly backgroundImage: string;
  readonly backgroundSize: string;
  readonly backgroundPosition: string;
} {
  const images: string[] = [];
  const sizes: string[] = [];
  const positions: string[] = [];
  for (const box of boxes) {
    const free = 1 - box.fw;
    const p = Math.abs(free) < 1e-9 ? 0 : box.gx / free;
    images.push("var(--rough-box)");
    sizes.push(`calc(${num(box.fw * 100)}% + ${num(box.aw)}px) ${num(box.height)}px`);
    positions.push(`calc(${num(p * 100)}% + ${num(box.bx + p * box.aw)}px) ${num(box.y)}px`);
  }
  return { backgroundImage: images.join(", "), backgroundSize: sizes.join(", "), backgroundPosition: positions.join(", ") };
}

/** The style a rough layer element writes to. */
export type RoughLayerTarget = {
  readonly style: Pick<CSSStyleDeclaration, "backgroundImage" | "backgroundSize" | "backgroundPosition">;
};

/** One column's rough layer element: paint boxes, clear them. */
export class RoughLayer {
  #shown = false;

  constructor(readonly $layer: RoughLayerTarget) {}

  /** Whether boxes are painted now. */
  get shown(): boolean {
    return this.#shown;
  }

  /** Paint `boxes` (three inline writes; call in a write phase). */
  paint(boxes: readonly RoughBox[]): void {
    const style = roughLayerStyle(boxes);
    this.$layer.style.backgroundImage = style.backgroundImage;
    this.$layer.style.backgroundSize = style.backgroundSize;
    this.$layer.style.backgroundPosition = style.backgroundPosition;
    this.#shown = true;
  }

  /** Remove the boxes (one write; nothing when none are painted). */
  clear(): void {
    if (!this.#shown) return;
    this.$layer.style.backgroundImage = "";
    this.#shown = false;
  }
}

/**
 * The boxes' tint (dom 65): the calendar's neutral event colour (the cells'
 * `--std-calendar-cell-background`, `--bg-surface-light`: black at 9 % light,
 * white at 6 % dark) at an opacity that reads as an event placeholder on the
 * day background (`--bg`: white, #1e222c), 3.24:1 light and 3.50:1 dark,
 * where the token itself reads 1.22:1 and 1.19:1. Neutral: no event colour.
 * The blur softens a box narrower than about twice its radius below that.
 */
export const ROUGH_TINT = "light-dark(rgb(0 0 0 / 0.44), rgb(255 255 255 / 0.38))";

/**
 * The layer's static rules (`CalendarSheetColumn`): neutral boxes, blurred,
 * never hit-tested, and clipped while the column's content is stale.
 * No `will-change`: a blurred layer in a moving column rasters once.
 */
export const ROUGH_LAYER_CSS = /* css */ `
  #rough {
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    pointer-events: none;
    background-repeat: no-repeat;
    filter: blur(${ROUGH_BLUR_PX}px);
    --rough-tint: ${ROUGH_TINT};
    --rough-box: linear-gradient(var(--rough-tint), var(--rough-tint));
  }

  :host([content-stale]) #rough {
    clip-path: inset(100%);
  }
`;

/**
 * Whether a loading column sweeps a shimmer over its rough layer, from the
 * `calendar-rough-shimmer` DOM flag (stage 2, dom 65; off by default;
 * `?flag:` or localStorage `debug-flag:`). Read when a column is made.
 */
export function roughShimmerEnabled(): boolean {
  return DomFlags.read("calendar-rough-shimmer", false);
}

/** The media query under which no sweep is built (`mountRoughSweep`) or shown (`ROUGH_SHIMMER_CSS`). */
export const REDUCED_MOTION_QUERY = "(prefers-reduced-motion: reduce)";

/**
 * The sweep's keyframes: a transform and nothing else, so the compositor
 * runs it. The research's microbenchmarks (research-progressive-columns
 * §4.1): a sibling transform sweep costs about 0.27 ms of GPU per frame;
 * one inside the blurred layer makes the blur a render pass every frame
 * (about 2 ms), and a `background-position` or per-box `opacity` animation
 * costs 7-13 ms of main thread per frame.
 */
export const ROUGH_SWEEP_KEYFRAMES: readonly { readonly offset: number; readonly transform: string }[] = [
  { offset: 0, transform: "translateX(-100%)" },
  { offset: 1, transform: "translateX(100%)" },
];

/** One sweep's duration (ms); it repeats until the column reveals. */
export const ROUGH_SWEEP_MS = 1400;

/** Whether the viewer asked for reduced motion (no `matchMedia`: no). */
export function prefersReducedMotion(): boolean {
  return globalThis.matchMedia?.(REDUCED_MOTION_QUERY).matches ?? false;
}

/** Where a sweep is mounted: the column's rough layer, which takes it as its next sibling. */
export type RoughSweepAnchor = Pick<ChildNode, "after">;

/**
 * Build a column's shimmer sweep as the next sibling of its rough layer
 * (`$rough`), never inside it: the blurred layer stays a still raster and
 * only the sweep moves. Nothing is built with the flag off or under reduced
 * motion. The sweep shows while its column loads (`ROUGH_SHIMMER_CSS`).
 */
export function mountRoughSweep(
  $rough: RoughSweepAnchor,
  options: { readonly enabled?: boolean; readonly reducedMotion?: boolean } = {},
): Option<HTMLElement> {
  if (!(options.enabled ?? roughShimmerEnabled()) || (options.reducedMotion ?? prefersReducedMotion())) return null;
  const $sweep = document.createElement("div");
  $sweep.id = "rough-sweep";
  $sweep.setAttribute("aria-hidden", "true");
  $rough.after($sweep);
  return $sweep;
}

/** The host attribute a column carries from its load (or recycle) until its reveal, while a sweep is mounted. */
export const ROUGH_LOADING_ATTRIBUTE = "rough-loading";

/**
 * The sweep's static rules (`CalendarSheetColumn`): hidden unless its column
 * loads (`rough-loading`, from the load or recycle to the reveal's write) or
 * holds stale content (placed ahead of its bind), so it reads as loading
 * before the data too. The moving band is the sweep's `::before`, clipped by
 * the sweep; inert, never hit-tested, and gone under reduced motion.
 */
export const ROUGH_SHIMMER_CSS = /* css */ `
  #rough-sweep {
    display: none;
    position: absolute;
    top: 0px;
    left: 0px;
    right: 0px;
    height: calc(100% - var(--column-tail-bottom, 12px));
    overflow: hidden;
    pointer-events: none;
  }

  :host([${ROUGH_LOADING_ATTRIBUTE}]) #rough-sweep,
  :host([content-stale]) #rough-sweep {
    display: block;
  }

  #rough-sweep::before {
    content: "";
    position: absolute;
    inset: 0px;
    background: linear-gradient(90deg, transparent, light-dark(rgb(0 0 0 / 0.06), rgb(255 255 255 / 0.08)), transparent);
    transform: ${ROUGH_SWEEP_KEYFRAMES[0]?.transform ?? "none"};
    animation: rough-sweep ${ROUGH_SWEEP_MS}ms linear infinite;
  }

  @keyframes rough-sweep {
${ROUGH_SWEEP_KEYFRAMES.map((frame) => `    ${frame.offset * 100}% { transform: ${frame.transform}; }`).join("\n")}
  }

  @media ${REDUCED_MOTION_QUERY} {
    :host([${ROUGH_LOADING_ATTRIBUTE}]) #rough-sweep,
    :host([content-stale]) #rough-sweep,
    #rough-sweep {
      display: none;
    }
  }
`;
